// ADR 24.9.26l §3 — the page's own title, when it knows a better one than its
// route's.
//
// A tiny external store rather than each page writing `document.title` itself:
// with two writers, the old "restore the previous title on unmount" put the
// *old* route's title back after the new route had set its own (passive
// cleanups run after the new route's effects). Here a page only ever sets or
// clears an override, and `RouteAnnouncer` is the one writer of
// `document.title`, composing `override ?? t(route's titleKey)`.

import { useEffect } from 'react'

let override: string | null = null
const listeners = new Set<() => void>()

/** The current page override, or `null` for the route's own title. */
export function getTitleOverride(): string | null {
  return override
}

/** `useSyncExternalStore`'s subscribe half. */
export function subscribeTitleOverride(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function setTitleOverride(next: string | null): void {
  if (next === override) return
  override = next
  for (const listener of listeners) listener()
}

/**
 * Name this page more precisely than its route does — a plan's title, a
 * walkthrough page's — while it is mounted. `null` (or an empty string) is
 * "nothing better yet", and the route's title stands.
 */
export function useDocumentTitle(title: string | null): void {
  useEffect(() => {
    setTitleOverride(title === '' ? null : title)
    return () => setTitleOverride(null)
  }, [title])
}
